import React, { ChangeEvent } from "react";
import { Link, Route, Switch } from "react-router-dom";
import ReactModal from "react-modal";
import { DateTime } from "luxon";
import { RouteComponentPropsHistory } from "../../types";

import withTitle from "../withTitle";
import withProps from "../withProps";
import { V1, V2 } from "../../Api";
import { WellKnownGroups, IAuthProps } from "../AuthContext";
import { QuestionnaireInnerEvent, QuestionnaireViewInner } from "./QuestionnaireView";
import NoteBox from "../NoteBox";
import withAuth from "../withAuth";
import { formatName, formatPhone, parseDateSafe } from "../../Utilities";
import ViewToolbar from "../ViewToolbar";
import ToolbarMenuItem from "../ToolbarMenuItem";

import { MessagingMessagesViewDetailOnly } from "./MessagingMessagesView";


interface IOutreachSurgicalConsultantViewState {
    isLoading: boolean | string;
    isLoadingError: boolean;
    isContactDialogOpen: boolean;
    isNotNeededDialogOpen: boolean;
    isTextDialogOpen: boolean;
    isEditDialogDialogOpen: boolean;
    isDialogLoading: boolean | string;
    isInputOnEmpty: boolean;
    canUseMessaging: boolean;

    messagingNumber: any;
    messageTemplates: any[];

    survey?: any;
    banner?: string;

    contactStatus: string;
    contactStartDate: string;
    contactStartTime: string;
    contactEndDate: string;
    contactEndTime: string;
    contactNote: string;
    contactFiles: string[];

    messageType: string;
    messageBody: string;
    messageBodyOriginal: string;

    editNameFirst: string;
    editNameLast: string;
    editBirthdate: string;
    editPhone: string;
    editInsurance: string;
}

interface IOutreachSurgicalConsultantViewProps extends RouteComponentPropsHistory<any, any>, IAuthProps {
    group: string;
}

class OutreachSurgicalConsultantView extends React.Component<IOutreachSurgicalConsultantViewProps, IOutreachSurgicalConsultantViewState> {
    private contactNoteRef: React.RefObject<NoteBox> = React.createRef();

    constructor(props: Readonly<IOutreachSurgicalConsultantViewProps>) {
        super(props);

        const state: IOutreachSurgicalConsultantViewState = {
            isLoading: false,
            isLoadingError: true,
            isContactDialogOpen: false,
            isNotNeededDialogOpen: false,
            isTextDialogOpen: false,
            isEditDialogDialogOpen: false,
            isDialogLoading: false,
            isInputOnEmpty: !!props.location.state?.inputOnEmpty,
            canUseMessaging: props.authContext.hasRight("msg:read") && props.authContext.hasRight("msg:send"),

            messagingNumber: {},
            messageTemplates: [],

            contactStatus: "",

            contactStartDate: "",
            contactStartTime: "",
            contactEndDate: "",
            contactEndTime: "",
            contactNote: "",
            contactFiles: [],

            messageType: "",
            messageBody: "",
            messageBodyOriginal: "",

            editNameFirst: "",
            editNameLast: "",
            editBirthdate: "",
            editPhone: "",
            editInsurance: "",
        };

        this.state = state;

        this.startOutreachQueue = this.startOutreachQueue.bind(this);
        this.openContactDialog = this.openContactDialog.bind(this);
        this.closeContactDialog = this.closeContactDialog.bind(this);
        this.openNotNeededDialog = this.openNotNeededDialog.bind(this);
        this.closeNotNeededDialog = this.closeNotNeededDialog.bind(this);
        this.openTextDialog = this.openTextDialog.bind(this);
        this.closeTextDialog = this.closeTextDialog.bind(this);
        this.submitTextDialog = this.submitTextDialog.bind(this);
        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.submitContactDialog = this.submitContactDialog.bind(this);
        this.handleDidNotReach = this.handleDidNotReach.bind(this);
        this.handleContactNote = this.handleContactNote.bind(this);
        this.handleContactNoteChange = this.handleContactNoteChange.bind(this);
        this.handleNotNeeded = this.handleNotNeeded.bind(this);
        this.handleOtherPractice = this.handleOtherPractice.bind(this);
        this.handleQuestionnaireContactNote = this.handleQuestionnaireContactNote.bind(this);
        this.sendToPatientCare = this.sendToPatientCare.bind(this);
        this.sendToSurgicalConsultant = this.sendToSurgicalConsultant.bind(this);
        this.toggleEditDialog = this.toggleEditDialog.bind(this);
        this.submitEditDialog = this.submitEditDialog.bind(this);
        this.handleInnerEvent = this.handleInnerEvent.bind(this);
    }

    public async componentDidMount() {
        if (this.state.canUseMessaging) {
            this.loadMessagingInfo();
        }

        if (this.props.match.params.id) {
            await this.loadSurvey(this.props.match.params.id);
        }
    }

    public componentDidUpdate(prevProps: Readonly<IOutreachSurgicalConsultantViewProps>) {
        if (this.props.match.params.id !== prevProps.match.params.id && typeof this.state.isLoading === "string") {
            // Clear error message
            this.setState({
                isLoading: false
            });
        }

        if (this.props.match.params.id !== prevProps.match.params.id && !!prevProps.match.params.id && prevProps.match.params.is !== "reminder") {
            // Remove reservation from previous
            V2.delete(`survey/outreach/group/${this.props.group}/${prevProps.match.params.id}/reservation`).catch(e => { });
        }
        if (this.props.match.params.id !== prevProps.match.params.id && !!this.props.match.params.id && this.props.match.params.id !== "reminder") {
            this.loadSurvey(this.props.match.params.id);
        }
    }

    public componentWillUnmount() {
        if (!!this.props.match.params.id && this.props.match.params.id !== "reminder") {
            // Remove any reservation
            V2.delete(`survey/outreach/group/${this.props.group}/${this.props.match.params.id}/reservation`).catch(e => { });
        }
    }

    public render() {
        return <Switch>
            <Route path={this.props.match.url + "/messaging/thread/:threadId"} component={MessagingMessagesViewDetailOnly} />
            <Route render={() => this.renderOutreach()} />;
        </Switch>;
    }

    public renderOutreach() {
        if (this.props.match.params.id === "reminder") {
            return this.renderMessageReminder();
        }
        else if (!!this.props.match.params.id) {
            return this.renderSurvey();
        }

        return (
            <div className="viewer-root">
                <div>
                    Use the <b>Start</b> button below to begin going through the outreach queue.
                </div>
                &nbsp;
                <div className="action-panel-dialog">
                    <button type="button" className="button" onClick={() => this.startOutreachQueue(false)}>Start</button>
                </div>
                &nbsp;
                {
                    this.state.isLoading === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isLoading === "string"
                            ? this.state.isLoadingError
                                ? <div className="error-text">{this.state.isLoading}</div>
                                : <div className="status-box slim">{this.state.isLoading}</div>
                            : null
                }
            </div>
        );
    }

    private renderSurvey() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }

        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-text">{this.state.isLoading}</div>
            </div>;
        }

        return <div className="viewer-root">
            <ViewToolbar>
                <ToolbarMenuItem right="survey:write">
                    <button type="button" className="icon-pencil" title="Update info..." onClick={this.toggleEditDialog} />
                </ToolbarMenuItem>
            </ViewToolbar>
            {this.state.isContactDialogOpen
                ? <ReactModal isOpen={this.state.isContactDialogOpen} onRequestClose={this.closeContactDialog}
                    overlayClassName="modal-overlay" className="modal-content modal-larger" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open" contentLabel="Contact dialog">
                    <form className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Contacted</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="contact-result">Contact result</label>
                                    <select id="contact-result" name="contactStatus" value={this.state.contactStatus} onChange={this.handleInputChanged}>
                                        <option value="">Select an option...</option>
                                        <option value="reachedmakeappointment">Reached patient, schedule an appointment</option>
                                        <option value="reacheddidntwant">Reached patient, did not want anything</option>
                                        <option value="calllater">Reached patient, contact again later</option>
                                        <option value="donotcontact">Do not contact again</option>
                                    </select>
                                </div>
                            </section>
                            &nbsp;
                            <section>
                                <div className="input-form-group">
                                    <label>Note {!this.props.authContext.hasRight("oreach:c:note:opt") ? <small>(required)</small> : <small>(optional)</small>}</label>
                                    <NoteBox ref={this.contactNoteRef} defaultNoteOpen={true} allowUpload={true} requireUpload={false} uploadCompress="image" hideLoading={true} stayOpenAfterSubmit={true} mode="single" onNewNote={this.handleContactNote} onNewNoteChange={this.handleContactNoteChange} />
                                </div>
                            </section>
                            {this.renderContactDialogInnerBody()}
                            {
                                this.state.isDialogLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : typeof this.state.isDialogLoading === "string"
                                        ? <div className="error-text"><br />{this.state.isDialogLoading}</div>
                                        : null
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <span className="spring" />
                                <button type="button" className="button green" onClick={this.submitContactDialog} disabled={this.isContactDialogButtonDisabled()}>Submit</button>
                                <button type="button" className="button" onClick={this.closeContactDialog}>Close</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                : null}
            {this.state.isNotNeededDialogOpen
                ? this.renderNotNeededDialog()
                : null}
            {this.state.isTextDialogOpen
                ? <ReactModal isOpen={this.state.isTextDialogOpen} onRequestClose={this.closeTextDialog}
                    overlayClassName="modal-overlay" className="modal-content modal-content-600" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open" contentLabel="Text message dialog">
                    <form className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Send text message &mdash; Using {formatPhone(this.state.messagingNumber.number)}</h3>
                            </header>

                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="message-type">Message</label>
                                    <select id="message-type" name="messageType" value={this.state.messageType} onChange={this.handleTextMessageType}>
                                        <option value="">Select a message template...</option>
                                        {this.state.messageTemplates.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                        <option value="custom">Custom message</option>
                                        <option value="sentother">Sent message on text thread</option>
                                    </select>
                                </div>
                            </section>
                            {this.state.messageType === "sentother" || (this.state.isDialogLoading && !this.state.messageBody)
                                ? null
                                : <section>
                                    <div className="input-form-group">
                                        <label htmlFor="message-sample">Message body</label>
                                        <textarea id="message-sample" className="input-textarea-medium" name="messageBody" value={this.state.messageBody} onChange={this.handleInputChanged} />
                                    </div>
                                </section>
                            }
                            {
                                this.state.isDialogLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : typeof this.state.isDialogLoading === "string"
                                        ? <div className="error-text"><br />{this.state.isDialogLoading}</div>
                                        : null
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <span className="spring" />
                                {this.state.messageType === "sentother"
                                    ? <button type="button" className="button green" onClick={this.submitTextDialog} disabled={!this.state.messageType}>Update Status</button>
                                    : <button type="button" className="button green" onClick={this.submitTextDialog} disabled={!this.state.messageType}>Send Text</button>}
                                <button type="button" className="button" onClick={this.closeTextDialog}>Close</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                : null}
            {this.state.isEditDialogDialogOpen
                ? <ReactModal isOpen={this.state.isEditDialogDialogOpen} onRequestClose={this.toggleEditDialog}
                    overlayClassName="modal-overlay" className="modal-content modal-content-600" portalClassName="modal-portal"
                    bodyOpenClassName="modal-open" contentLabel="Edit information">
                    <form className="input-form">
                        <section className="info-block">
                            <header>
                                <h3>Update/Correct information</h3>
                            </header>
                            <p>
                                You can update and correct some of the information for this patient if it is no longer accurate.
                            </p>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-first">First Name</label>
                                    <input type="text" id="frm-name-first" name="editNameFirst" value={this.state.editNameFirst} onChange={this.handleInputChanged} autoComplete="off" />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-last">Last Name</label>
                                    <input type="text" id="frm-name-last" name="editNameLast" value={this.state.editNameLast} onChange={this.handleInputChanged} autoComplete="off" />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-dob">Birthdate</label>
                                    <input type="date" id="frm-dob" name="editBirthdate" value={this.state.editBirthdate} onChange={this.handleInputChanged} autoComplete="off" />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-phone">Phone Number</label>
                                    <input type="tel" id="frm-phone" name="editPhone" value={this.state.editPhone} onChange={this.handleInputChanged} autoComplete="off" />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-insurance">Insurance</label>
                                    <input type="text" id="frm-insurance" name="editInsurance" value={this.state.editInsurance} onChange={this.handleInputChanged} />
                                </div>
                            </section>
                            {
                                this.state.isDialogLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : typeof this.state.isDialogLoading === "string"
                                        ? <div className="error-text"><br />{this.state.isDialogLoading}</div>
                                        : null
                            }
                            &nbsp;
                            <div className="action-panel-dialog">
                                <span className="spring" />
                                <button type="button" className="button green" onClick={this.submitEditDialog}>Save</button>
                                <button type="button" className="button" onClick={this.toggleEditDialog}>Close</button>
                            </div>
                        </section>
                    </form>
                </ReactModal>
                : null}
            {this.state.banner
                ? <div className="status-box slim">{this.renderBanner(this.state.banner)}</div>
                : null}
            {this.state.survey
                ? <QuestionnaireViewInner
                    survey={this.state.survey}
                    displayContactInfo={true}
                    userDisplayName={this.props.authContext.user.displayName}
                    submitContactNote={this.handleQuestionnaireContactNote}
                    canViewMessages={this.props.authContext.hasRight("msg:read")}
                    onInnerEvent={this.handleInnerEvent}
                />
                : null}
            {!(this.state.isContactDialogOpen || this.state.isEditDialogDialogOpen || this.state.isTextDialogOpen) && this.state.isDialogLoading === true
                ? <div className="spinner lds-dual-ring" />
                : !this.state.isContactDialogOpen && typeof this.state.isDialogLoading === "string"
                    ? <div className="error-text">{this.state.isDialogLoading}</div>
                    : null}
            &nbsp;
            <div className="action-panel-dialog">
                {this.state.survey?.source === 3
                    ? this.props.group === WellKnownGroups.SurgicalConsultant
                        ? <button type="button" className="button text-with-icon" onClick={this.sendToPatientCare}><i className="icon-mail-forward" /><span>Send to Patient Care Rep</span></button>
                        : this.props.group === WellKnownGroups.PatientCareRep
                            ? <button type="button" className="button text-with-icon" onClick={this.sendToSurgicalConsultant}><i className="icon-mail-forward" /><span>Send to Surgical Consultant</span></button>
                            : null
                    : null}
                {/*<Link to={{ pathname: "/appointments/create", state: { survey: this.state.survey } }} className="button text-with-icon green"><i className="icon-plus" /><span>New Appointment</span></Link>*/}
                <span className="spring" />
                {this.state.survey?.contactAllowed && this.state.canUseMessaging ? <button type="button" className="button text-with-icon push-right" onClick={this.openTextDialog} disabled={!this.state.survey.phone}><i className="icon-mobile" /><span>Send Text</span></button> : null}
                <button type="button" className="button text-with-icon" onClick={this.openContactDialog}><i className="icon-phone" /><span>Contacted</span></button>
                <button type="button" className="button text-with-icon yellow" onClick={this.handleDidNotReach}><i className="icon-times" /><span>Did Not Reach</span></button>
                {this.state.survey?.source === 3
                    ? <button type="button" className="button text-with-icon red" onClick={this.openNotNeededDialog}><i className="icon-times" /><span>Not Needed Until...</span></button>
                    : null}
                {this.state.survey?.source === 3
                    ? <button type="button" className="button text-with-icon" onClick={this.handleOtherPractice}><i className="icon-minus" /><span>Handled Elsewhere</span></button>
                    : null}
            </div>
        </div>;
    }

    private renderMessageReminder() {
        return <div className="viewer-root">
            <div>
                <i className="icon-envelope" /> <b>You have unread messages.</b><br />
                <br />
                You must go handle them before you can continue going through the queue.
                </div>
                &nbsp;
                <div className="action-panel-dialog">
                <Link to="/reminders/messages" className="button">Go to Messages</Link>
            </div>
        </div>;
    }

    private renderNotNeededDialog() {
        const contactDate = parseDateSafe(this.state.contactStartDate);
        const inPast = contactDate && contactDate <= DateTime.local();

        return <ReactModal isOpen={this.state.isNotNeededDialogOpen} onRequestClose={this.closeNotNeededDialog}
            overlayClassName="modal-overlay" className="modal-content modal-content-400" portalClassName="modal-portal"
            bodyOpenClassName="modal-open" contentLabel="Not needed dialog">
            <form className="input-form">
                <section className="info-block">
                    <header>
                        <h3>Not needed until...</h3>
                    </header>
                    <section>
                        <div className="input-form-group">
                            <label>Note {!this.props.authContext.hasRight("oreach:c:note:opt") ? <small>(required)</small> : <small>(optional)</small>}</label>
                            <NoteBox ref={this.contactNoteRef} defaultNoteOpen={true} allowUpload={true} requireUpload={false} uploadCompress="image" hideLoading={true} stayOpenAfterSubmit={true} mode="single" onNewNote={this.handleContactNote} onNewNoteChange={this.handleContactNoteChange} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="contact-start-date">Show back up after</label>
                            <input type="date" id="contact-start-date" name="contactStartDate" value={this.state.contactStartDate} onChange={this.handleInputChanged} />
                        </div>
                    </section>
                    {contactDate && contactDate.diffNow("months")?.months > 18
                        ? <>&nbsp;<div className="warning-box">The specified date is <b>{Math.floor(contactDate.diffNow("months").months).toFixed(0)}</b> months from now. Please make sure this is correct.</div></>
                        : contactDate && contactDate <= DateTime.local()
                            ? <>&nbsp;<div className="error-box">The specified date is in the past! You must choose a future date.</div></>
                            : null}
                    {
                        this.state.isDialogLoading === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.isDialogLoading === "string"
                                ? <div className="error-text"><br />{this.state.isDialogLoading}</div>
                                : null
                    }
                            &nbsp;
                            <div className="action-panel-dialog">
                        <span className="spring" />
                        <button type="button" className="button green" onClick={this.submitContactDialog} disabled={this.isContactDialogButtonDisabled() || inPast}>Submit</button>
                        <button type="button" className="button" onClick={this.closeNotNeededDialog}>Close</button>
                    </div>
                </section>
            </form>
        </ReactModal>;
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>, callback?: () => void) {
        const name = e.currentTarget.name;
        const value: string | boolean | any = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            [name]: value
        } as any, (callback && typeof callback === "function") ? callback : undefined);
    }

    private handleTextMessageType = (e: ChangeEvent<HTMLSelectElement>) => this.handleInputChanged(e, this.loadRenderedTemplate);

    private async startOutreachQueue(replace: boolean) {
        // Request the top of the queue
        try {
            this.setState({
                isLoadingError: true,
                isLoading: true
            });

            if (this.state.canUseMessaging) {
                // Check for unread message threads
                const numberResult = await V2.get<any>(`messaging/threads/by-number/${this.state.messagingNumber.number}/has-unread`);
                if (numberResult.hasUnread) {
                    // Show message to check messages
                    (replace ? this.props.history.replace : this.props.history.push)(`/${this.groupToUrl(this.props.group)}/outreach/survey/reminder`);

                    return;
                }
            }

            const item = await V2.get<any>(`survey/outreach/group/${this.props.group}/select`);

            this.setState({
                isLoading: false
            }, () => (replace ? this.props.history.replace : this.props.history.push)(`/${this.groupToUrl(this.props.group)}/outreach/survey/${item.id}`, { banner: item.banner }));
        }
        catch (e) {
            if (e.statusCode === 404) {
                if (this.state.isInputOnEmpty) {
                    // Input queue
                    this.props.history.replace(`/${this.groupToUrl(this.props.group)}/lead-input`);
                }
                else {
                    // Regular queue
                    this.setState({
                        isLoadingError: false,
                        isLoading: "The queue is empty"
                    }, () => this.props.history.replace(`/${this.groupToUrl(this.props.group)}/outreach`));
                }
                return;
            }

            this.setState({
                isLoading: e.message || e.error || "Could not start queue"
            });
        }
    }

    private async loadSurvey(id: string) {
        try {
            const survey = await V2.get<any>(`survey/${id}?questions=full_contact&reserve=true`);

            this.setState({
                survey,
                banner: this.props.location.state?.banner,
                isLoading: false,

                contactStatus: "",

                contactStartDate: "",
                contactStartTime: "",
                contactEndDate: "",
                contactEndTime: "",
                contactNote: "",
                contactFiles: [],
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e.error || "Could not load survey"
            });
        }
    }

    private async loadMessagingInfo() {
        try {
            let templateType;
            switch (this.props.group) {
                case WellKnownGroups.AppointmentSpecialist: templateType = "ahs"; break;
                case WellKnownGroups.SurgicalConsultant: templateType = "surgconsult"; break;
                case WellKnownGroups.PatientCareRep: templateType = "pcr"; break;
                default: templateType = ""; break;
            }

            if (!templateType) {
                // No known template type, treat it the same as not having messaging access
                this.setState({
                    canUseMessaging: false,
                });

                return;
            }

            var [results, templates] = await Promise.all([
                V2.get<any>("messaging/numbers/own"),
                V2.get<any>("messaging/templates?type=ahs"),
            ]);

            if (results.defaultNumber) {
                this.setState({
                    messagingNumber: results.defaultNumber
                });
            }
            else {
                // No number, treat it the same as not having messaging access
                this.setState({
                    canUseMessaging: false,
                    messagingNumber: {},
                });

                return;
            }

            this.setState({
                messageTemplates: templates.templates
            });
        }
        catch (e) {
            // Something went wrong
            this.setState({
                canUseMessaging: false,
                messagingNumber: {},
            });
        }
    }

    private openContactDialog() {
        this.setState({
            isContactDialogOpen: true
        });
    }

    private closeContactDialog() {
        this.setState({
            isContactDialogOpen: false,
            isDialogLoading: false,

            contactStatus: "",
            contactStartDate: "",
            contactStartTime: "",
            contactEndDate: "",
            contactEndTime: "",
        });
    }

    private renderContactDialogInnerBody() {
        switch (this.state.contactStatus) {
            case "reachedmakeappointment":
                return <section>
                    <span>You will be sent to the appointment creation page after submission.</span>
                </section>;
            case "calllater":
                return <>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="contact-start-date">Range Start Date</label>
                            <input type="date" id="contact-start-date" name="contactStartDate" value={this.state.contactStartDate} onChange={this.handleInputChanged} />
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="contact-start-time">Range Start Time</label>
                            <input type="time" id="contact-start-time" name="contactStartTime" value={this.state.contactStartTime} onChange={this.handleInputChanged} />
                        </div>
                    </section>
                    <section>
                        <div className="input-form-group">
                            <label htmlFor="contact-end-date">Range End Date</label>
                            <input type="date" id="contact-end-date" name="contactEndDate" value={this.state.contactEndDate} onChange={this.handleInputChanged} />
                        </div>
                        <div className="input-form-group">
                            <label htmlFor="contact-end-time">Range End Time</label>
                            <input type="time" id="contact-end-time" name="contactEndTime" value={this.state.contactEndTime} onChange={this.handleInputChanged} />
                        </div>
                    </section>
                </>;
            default:
                return null;
        }
    }

    private openNotNeededDialog() {
        this.setState({
            isNotNeededDialogOpen: true,
            contactStatus: "notneeded"
        });
    }

    private closeNotNeededDialog() {
        this.setState({
            isNotNeededDialogOpen: false,
            isDialogLoading: false,

            contactStatus: "",
            contactStartDate: "",
            contactEndDate: "",
        });
    }

    private openTextDialog() {
        const templates = this.state.messageTemplates;

        this.setState({
            isTextDialogOpen: true,
            messageType: templates.length === 1 ? templates[0].id : ""
        }, () => {
            if (this.state.messageType) {
                this.loadRenderedTemplate();
            }
        });
    }

    private closeTextDialog() {
        this.setState({
            isTextDialogOpen: false,
            isDialogLoading: false,
        });
    }

    private async submitTextDialog() {
        try {
            this.setState({
                isDialogLoading: true
            });

            const data = {
                templateId: ["custom", "sentother"].includes(this.state.messageType)
                    ? undefined
                    : this.state.messageType,
                numberId: this.state.messagingNumber.id,
                messageBody: this.state.messageBody !== this.state.messageBodyOriginal || this.state.messageType === "custom"
                    ? this.state.messageBody
                    : undefined,
            };

            if (!data.templateId && !data.messageBody) {
                // Just do a status update
                const updateData = {
                    status: "textsent",
                    note: "Sent message on text thread",
                };
                await V2.put<void>(`survey/outreach/group/${this.props.group}/${this.state.survey.id}/contact`, updateData);
            }
            else {
                await V2.post<void>(`survey/outreach/group/${this.props.group}/${this.state.survey.id}/send-text`, data);
            }

            this.setState({
                isDialogLoading: false
            });

            this.closeTextDialog();
            this.startOutreachQueue(true);
        }
        catch (e) {
            this.setState({
                isDialogLoading: e.message || e.error || "Could not save contact status"
            });
        }
    }

    private async loadRenderedTemplate() {
        if (!this.state.survey?.id) {
            return;
        }

        if (!this.state.messageType || ["custom", "sentother"].includes(this.state.messageType)) {
            this.setState({
                messageBody: "",
                messageBodyOriginal: "",
            });

            return;
        }

        try {
            this.setState({
                isDialogLoading: true,
                messageBody: "",
                messageBodyOriginal: "",
            });

            const result = await V2.get<any>(`survey/outreach/group/${this.props.group}/render-template?template=${this.state.messageType}&survey=${this.state.survey?.id}`);

            this.setState({
                isDialogLoading: false,
                messageBody: result.result,
                messageBodyOriginal: result.result,
            });
        }
        catch (e) {
            this.setState({
                isDialogLoading: e.message || "Could not load message template"
            });
        }
    }

    private isContactDialogButtonDisabled() {
        let base = !this.props.authContext.hasRight("oreach:c:note:opt") && !this.state.contactNote?.trim();
        switch (this.state.contactStatus) {
            case "calllater": return base || !this.state.contactStartDate || !this.state.contactStartTime;
            case "notneeded": return base || !this.state.contactStartDate;
            default: return base || !this.state.contactStatus;
        }
    }

    private async submitContactDialog() {
        try {
            this.setState({
                isDialogLoading: true
            });

            const data: any = {
                status: this.state.contactStatus
            };

            if (this.state.contactFiles.length === 0) {
                // Process note and upload files
                if (await this.contactNoteRef.current?.submit() === false) {
                    this.setState({
                        isDialogLoading: false,
                    });
                    return;
                }
            }

            if (this.state.contactNote) {
                data.note = this.state.contactNote;
                data.attachments = this.state.contactFiles;
            }

            if (this.state.contactStatus === "calllater") {
                data.callbackStart = `${this.state.contactStartDate}T${this.state.contactStartTime}`;
                data.callbackEnd = this.state.contactEndDate && this.state.contactEndTime
                    ? `${this.state.contactEndDate}T${this.state.contactEndTime}`
                    : undefined;
            }
            else if (this.state.contactStatus === "notneeded" && this.state.contactStartDate) {
                data.callbackStart = `${this.state.contactStartDate}T00:00:00Z`;
            }

            await V2.put<any>(`survey/outreach/group/${this.props.group}/${this.state.survey.id}/contact`, data);

            this.setState({
                isDialogLoading: false,
            });

            switch (data.status) {
                case "reachedmakeappointment":
                    // Send to appointment creation
                    this.closeContactDialog();
                    this.props.history.replace("/appointments/create", { survey: this.state.survey, allowNoProof: true });
                    break;
                default:
                    // Continue on with queue
                    this.closeContactDialog();
                    this.closeNotNeededDialog();
                    this.startOutreachQueue(true);
                    break;
            }
        }
        catch (e) {
            this.setState({
                isDialogLoading: e.message || e.error || "Could not save contact status"
            });
        }
    }

    private handleDidNotReach() {
        this.setState({
            contactStatus: "didnotreach"
        }, () => this.submitContactDialog());
    }

    private handleNotNeeded() {
        this.setState({
            contactStatus: "notneeded"
        }, () => this.submitContactDialog());
    }

    private handleOtherPractice() {
        this.setState({
            contactStatus: "otherpractice"
        }, () => this.submitContactDialog());
    }

    private sendToPatientCare() {
        this.sendToGroup(WellKnownGroups.PatientCareRep);
    }

    private sendToSurgicalConsultant() {
        this.sendToGroup(WellKnownGroups.SurgicalConsultant);
    }

    private async sendToGroup(newGroup: string) {
        try {
            this.setState({
                isDialogLoading: true
            });

            await V2.put(`survey/outreach/group/${this.props.group}/leads/${this.state.survey.id}/set-group/${newGroup}`);

            this.setState({
                isDialogLoading: false,
            }, () => this.startOutreachQueue(true));
        }
        catch (e) {
            this.setState({
                isDialogLoading: e.message || "Could not move groups"
            });
        }
    }

    private groupToUrl(group: string) {
        switch (group) {
            case WellKnownGroups.SurgicalConsultant: return "surgicalconsult";
            case WellKnownGroups.PatientCareRep: return "telemarketing";
            case WellKnownGroups.AppointmentSpecialist: return "appointmentspecialist";
            default: throw new Error("Unknown group " + group);
        }
    }

    private async handleContactNote(body: string, files?: string[]) {
        this.setState({
            contactNote: body,
            contactFiles: files || []
        })
    }

    private handleContactNoteChange(body: string) {
        this.setState({
            contactNote: body
        });
    }

    private renderBanner(banner: string) {
        switch (banner) {
            case "oncall": return "This is from the on-call queue";
            case "lead_light": return "This is from a nurse's lead";
            case "lead": return "This is from the annual report";
            default: return banner;
        }
    }

    private async handleQuestionnaireContactNote(body: string, files?: string[]) {
        const data = {
            body,
            files
        };

        const noteResult = await V2.post<any>(`survey/${this.state.survey.id}/notes/contact/${this.props.group}`, data);
        noteResult.author = formatName(noteResult.employee.name);

        const survey = this.state.survey;
        const notes = survey.contactNotes;
        notes.push(noteResult);

        this.setState({
            survey
        });
    }

    private toggleEditDialog() {
        const isEditDialogDialogOpen = this.state.isEditDialogDialogOpen;

        if (!isEditDialogDialogOpen) {
            // Load information
            this.setState({
                editNameFirst: this.state.survey.name.first,
                editNameLast: this.state.survey.name.last,
                editBirthdate: this.state.survey.birthdate?.substring(0, 10) || "",
                editPhone: this.state.survey.phone,
                editInsurance: this.state.survey.insurance.provider,
            });
        }

        this.setState({
            isEditDialogDialogOpen: !isEditDialogDialogOpen
        });
    }

    private async submitEditDialog() {
        try {
            this.setState({
                isDialogLoading: true
            });

            const {
                editNameFirst,
                editNameLast,
                editBirthdate,
                editPhone,
                editInsurance,
                survey,
            } = this.state;

            const data: any = {};

            if (editNameFirst !== survey.name.first || editNameLast !== survey.name.last) {
                data.name = {
                    first: editNameFirst,
                    last: editNameLast
                };
            }
            if (editBirthdate !== survey.birthdate) {
                data.birthdate = editBirthdate;
            }
            if (editPhone !== survey.name.phone) {
                data.phone = editPhone.replace(/[^\d]/g, "");
            }
            if (editInsurance !== survey.insurance?.provider) {
                data.insurance = {
                    provider: editInsurance
                };
            }

            await V1.patch<void>(`survey/${this.state.survey.id}`, data);

            // Update visible survey info so we don't have to reload it
            survey.name.first = editNameFirst;
            survey.name.last = editNameLast;
            survey.birthdate = editBirthdate;
            survey.phone = editPhone;
            survey.insurance.provider = editInsurance;

            this.setState({
                isDialogLoading: false,
                isEditDialogDialogOpen: false,

                survey
            });
        }
        catch (e) {
            this.setState({
                isDialogLoading: e.message || "Could not save changes"
            });
        }
    }

    private handleInnerEvent(type: QuestionnaireInnerEvent, data: any) {
        if (type === "outreach_text_nav") {
            // We handle this differently so we can keep the reservation
            this.props.history.push(this.props.match.url + `/messaging/thread/${data.threadId}`);

            return true;
        }
    }
}

export default withTitle("Surgical Consultant Outreach", withAuth(withProps(OutreachSurgicalConsultantView, { group: WellKnownGroups.SurgicalConsultant })));
export const OutreachTelemarketerView = withTitle("Patient Care Rep. Outreach", withAuth(withProps(OutreachSurgicalConsultantView, { group: WellKnownGroups.PatientCareRep })));
export const OutreachAppointmentSpecialistView = withTitle("Appointment Specialist Outreach", withAuth(withProps(OutreachSurgicalConsultantView, { group: WellKnownGroups.AppointmentSpecialist })));
